import {
  type CellContext,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect, useMemo, useState } from 'react'

import { type DeductionMethod } from '../types'
import { getLocaleCurrencyStringFromString } from '../utils'
import { DownloadEmployerInvoiceDropdown } from './DownloadEmployerInvoiceDropdown'
import EmployerInvoiceBillingSummary from './EmployerInvoiceBillingSummary'
import EmployerInvoiceMemberSummary from './EmployerInvoiceMemberSummary'
import {
  EmployerInvoiceContext,
  type EmployerInvoiceData,
  type EmployerInvoiceType,
  type EnrichedMemberInvoice,
  enrichMemberInvoices,
} from '../utils/employer_invoice_utils'
import { type ReportingReport } from '../utils/web_api.schemas'
import OpacityTransition from './reusable/OpacityTransition'
import QuestionMarkTooltip from './reusable/QuestionMarkTooltip'
import { SortableHeader } from './reusable/table/SortableHeader'
import TanstackTable from './reusable/table/TanstackTable'
import { TanstackTableGlobalSearch } from './reusable/table/TanstackTableGlobalSearch'

/*
 ****************** Types ******************
 */

export type EmployerInvoiceProps = {
  employer_invoice_id: string;
  deduction_method: DeductionMethod;
}

/*
 ****************** Tooltips ******************
 */

const MemberExpenseBreakdownTooltip = () => {
  return (
    <QuestionMarkTooltip>
      <div
        style={{ zIndex: 100, width: '22rem' }}
        className='text-xs p-2 w-60 rounded-md text-center'
      >
        This is a breakdown of your expenses grouped by employee. Click on an
        employee to see a more detailed summary of their expenses, including
        anticipated insurance premium payments.
      </div>
    </QuestionMarkTooltip>
  )
}

/*
 ****************** Row Outputs ******************
 */

const EmployeeInvoicePlanYear = (
  info: CellContext<EnrichedMemberInvoice, number | undefined>
) => {
  const { getValue } = info
  return (
    <div className='text-sm font-normal'>
      {getValue()}
    </div>
  )
}

const EmployeeInvoiceScheduledPaymentsTotal = (
  info: CellContext<EnrichedMemberInvoice, string>
) => {
  const { getValue } = info
  return (
    <div className='text-sm font-normal'>
      {getLocaleCurrencyStringFromString(getValue())}
    </div>
  )
}

const EmployeeInvoiceCoverageCorrectionsTotal = (
  info: CellContext<EnrichedMemberInvoice, string>
) => {
  const { getValue } = info
  return (
    <div className='text-sm font-normal'>
      {getLocaleCurrencyStringFromString(getValue())}
    </div>
  )
}

const EmployeeInvoiceOtherTotal = (
  info: CellContext<EnrichedMemberInvoice, string>
) => {
  const { getValue } = info
  return (
    <div className='text-sm font-normal'>
      {getLocaleCurrencyStringFromString(getValue())}
    </div>
  )
}

const EmployeeInvoiceTotal = (info: CellContext<EnrichedMemberInvoice, string>) => {
  const { getValue } = info
  return (
    <div className='text-sm font-normal'>
      {getLocaleCurrencyStringFromString(getValue())}
    </div>
  )
}

/*
 ****************** Column Helper ******************
 */

const columnHelper = createColumnHelper<EnrichedMemberInvoice>()

const columns = [
  columnHelper.accessor('employee_id', {
    id: 'employeeId',
    header: 'Employee ID',
    cell: (info) => (info.getValue()),
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('full_name', {
    id: 'fullName',
    cell: (info) => (
      <div className='text-sm font-normal'>{info.getValue()}</div>
    ),
    header: (info) => (
      <SortableHeader<EnrichedMemberInvoice> {...info} headerText='Full Name' />
    ),
    footer: (info) => info.column.id,
    enableGlobalFilter: true,
  }),
  columnHelper.accessor('plan_year', {
    id: 'planYear',
    cell: (info) => <EmployeeInvoicePlanYear {...info} />,
    header: (info) => (
      <SortableHeader<EnrichedMemberInvoice> {...info} headerText='Plan Year' />
    ),
    footer: (info) => info.column.id,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor(
    'amounts.total_insurance_premiums.value',
    {
      id: 'scheduledPayments',
      cell: (info) => <EmployeeInvoiceScheduledPaymentsTotal {...info} />,
      header: (info) => (
        <SortableHeader<EnrichedMemberInvoice>
          {...info}
          headerText='Scheduled Payments'
        />
      ),
      footer: (info) => info.column.id,
      enableGlobalFilter: false,
    }
  ),
  columnHelper.accessor('amounts.coverage_corrections.value', {
    id: 'coverageCorrections',
    cell: (info) => <EmployeeInvoiceCoverageCorrectionsTotal {...info} />,
    header: (info) => (
      <SortableHeader<EnrichedMemberInvoice>
        {...info}
        headerText='Coverage Corrections'
      />
    ),
    footer: (info) => info.column.id,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('amounts.other.value', {
    id: 'other',
    cell: (info) => <EmployeeInvoiceOtherTotal {...info} />,
    header: (info) => (
      <SortableHeader<EnrichedMemberInvoice> {...info} headerText='Other' />
    ),
    footer: (info) => info.column.id,
    enableGlobalFilter: false,
  }),
  columnHelper.accessor('amounts.total.value', {
    id: 'total',
    cell: (info) => <EmployeeInvoiceTotal {...info} />,
    header: (info) => (
      <SortableHeader<EnrichedMemberInvoice> {...info} headerText='Total' />
    ),
    footer: (info) => info.column.id,
    enableGlobalFilter: false,
  }),
]

/*
 ****************** Employer Invoice Component ******************
 */

function EmployerInvoice ({
  employer_invoice_id: employerInvoiceId,
}: EmployerInvoiceProps) {
  const [memberInvoices, setMemberInvoices] = useState<EnrichedMemberInvoice[]>([])
  const [latestReport, setLatestReport] = useState<ReportingReport | null>()

  const memberInvoiceById = useMemo<{ [id: string]: EnrichedMemberInvoice }>(() => memberInvoices.reduce((acc, memberInvoice) => {
    return {
      ...acc,
      [memberInvoice.id]: memberInvoice,
    }
  }, {}), [memberInvoices])
  const [employerInvoiceData, setEmployerInvoiceData] =
    useState<EmployerInvoiceType>({
      id: '',
      status: '',
      period_end_date: '',
      period_start_date: '',
      total: { value: '0', currency: 'USD' },
      member_invoices: [],
      amounts: {
        premiums: { value: '0', currency: 'USD' },
        prefunding: { value: '0', currency: 'USD' },
        other: { value: '0', currency: 'USD' },
        insurance_premiums_total: { value: '0', currency: 'USD' },
        coverage_corrections: { value: '0', currency: 'USD' },
      },
      plan_years: []
    })
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [targetMemberInvoice, setTargetMemberInvoice] = useState<string | null>(null)

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'planYear', desc: false }, { id: 'fullName', desc: false },
  ])

  const onRowClick = ({ row }: { row: Row<EnrichedMemberInvoice> }) => {
    setTargetMemberInvoice(row.original.id)
  }

  const showPlanYear = employerInvoiceData.plan_years.length > 1

  const table = useReactTable({
    data: memberInvoices,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    autoResetAll: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    state: {
      sorting,
      columnVisibility: {
        // Include employeeId as a column to allow for filtering, but don't display it in the table
        employeeId: false,
        fullName: true,
        planYear: showPlanYear,
        scheduledPayments: true,
        coverageCorrections: true,
        other: true,
        total: true
      }
    },
    meta: {
      setSorting,
    },
    globalFilterFn: (rows, columnId, filterValue) => {
      const columnValue = rows.getValue(columnId)
      if (typeof columnValue === 'string') {
        if (columnId === 'employeeId') {
          // Do an exact match for employee ID
          return columnValue === filterValue
        }
        return columnValue.toLowerCase().includes(filterValue.toLowerCase())
      }
      return false
    }
  })

  /*
   ****************** API Calls ******************
   */

  const fetchEmployerInvoiceById = async (id: string) => {
    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(`/api/employer_invoices/${id}`, { // eslint-disable-line thatch/no-bare-fetch
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
      const json = await response.json()
      if (json.status !== 'ok') throw new Error('API error')

      setMemberInvoices(enrichMemberInvoices(json.data as EmployerInvoiceData))
      setEmployerInvoiceData(json.data.employer_invoice)
      setLatestReport(json.data.latest_report)
      setIsLoading(false)
    } catch (e: any) {
      setIsLoading(false)
      setIsError(true)
      console.error(e)
    }
  }

  useEffect(() => {
    fetchEmployerInvoiceById(employerInvoiceId)
  }, [employerInvoiceId])

  /*
   ****************** Invoice Output JSX ******************
   */

  if (isError) {
    return (
      <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 text-sm rounded-lg shadow-md'>
        <div>
          There was an error fetching your employer invoice. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  if (targetMemberInvoice) {
    return (
      <OpacityTransition key='member-summary'>
        <EmployerInvoiceContext.Provider value={{ setTargetMemberInvoice }}>
          <EmployerInvoiceMemberSummary
            memberInvoice={memberInvoiceById[targetMemberInvoice]}
          />
        </EmployerInvoiceContext.Provider>
      </OpacityTransition>
    )
  }

  return (
    <OpacityTransition key='employer-invoice-summary'>
      <EmployerInvoiceContext.Provider value={{ setTargetMemberInvoice }}>
        <div>
          {/* ******************** Billing Summary ******************** */}

          <EmployerInvoiceBillingSummary
            employerInvoiceData={employerInvoiceData}
            isLoading={isLoading}
          />
          {/* ******************** Table Output ******************** */}

          <div className='mt-12 h2'>
            <div className='flex align-center'>
              <div>Member Expense Breakdown</div>
              <div className='ml-2'>
                <MemberExpenseBreakdownTooltip />
              </div>
            </div>
            <div className='h-4' />
            <div className='sm:flex sm:items-center sm:justify-between'>
              <div className='sm:w-1/2 sm:max-w-sm mb-4 sm:mb-0'>
                <TanstackTableGlobalSearch
                  table={table}
                  placeholder='Find employees'
                />
              </div>
              <DownloadEmployerInvoiceDropdown
                filteredRows={table.getFilteredRowModel().rows.map(row => row.original)}
                employerInvoiceId={employerInvoiceId}
                periodStartDate={employerInvoiceData.period_start_date}
                periodEndDate={employerInvoiceData.period_end_date}
                reportDownloadUrl={latestReport?.download_url}
                showPlanYear={showPlanYear}
              />
            </div>
            <div className='h-4' />
            <TanstackTable
              table={table}
              onRowClick={onRowClick}
              isLoading={isLoading}
              paginationLabelSingular='invoice'
            />
          </div>
        </div>
      </EmployerInvoiceContext.Provider>
    </OpacityTransition>
  )
}

export default EmployerInvoice
